import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import {
  createNotification as apiCreateNotification,
  getNotifications as apiGetNotifications,
  updateNotification as apiUpdateNotification,
  dismissNotification as apiDismissNotification,
  deleteNotification as apiDeleteNotification,
  clearAllNotifications as apiClearAllNotifications,
} from '../services/api';
import { parseApiDate } from '../utils/parseApiDate';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { keycloak, initialized } = useKeycloak();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load notifications from backend on mount and when keycloak is initialized
  useEffect(() => {
    if (initialized && keycloak?.authenticated) {
      loadNotifications();
    } else {
      setLoading(false);
    }
  }, [initialized, keycloak?.authenticated]);

  const loadNotifications = useCallback(async () => {
    if (!keycloak?.authenticated) return;
    try {
      setLoading(true);
      const data = await apiGetNotifications(keycloak, null, 100);
      // Convert backend format to frontend format
      const formatted = data.map((n) => ({
        id: n.id,
        type: n.type,
        title: n.title,
        message: n.message,
        progress: n.progress,
        status: n.status,
        metadata: n.metadata || {},
        dismissed: n.dismissed,
        timestamp: parseApiDate(n.timestamp) || new Date(),
        dismissible: true,
      }));
      setNotifications(formatted);
    } catch (error) {
      console.error('Failed to load notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [keycloak]);

  const addNotification = useCallback(async (notification) => {
    const newNotification = {
      type: 'info',
      title: '',
      message: '',
      progress: null,
      status: 'pending',
      metadata: {},
      ...notification,
    };

    // Optimistically add to local state
    const tempId = `temp_${Date.now()}_${Math.random()}`;
    const optimisticNotification = {
      id: tempId,
      ...newNotification,
      timestamp: new Date(),
      dismissible: true,
      dismissed: false,
    };
    setNotifications((prev) => [...prev, optimisticNotification]);

    // Save to backend if authenticated
    if (keycloak?.authenticated) {
      try {
        const saved = await apiCreateNotification(keycloak, {
          type: newNotification.type,
          title: newNotification.title,
          message: newNotification.message,
          progress: newNotification.progress,
          status: newNotification.status,
          extra_data: newNotification.metadata,
        });
        // Replace temp notification with saved one
        setNotifications((prev) =>
          prev.map((n) => (n.id === tempId ? { ...optimisticNotification, id: saved.id, timestamp: parseApiDate(saved.timestamp) || new Date() } : n))
        );
        return saved.id;
      } catch (error) {
        console.error('Failed to save notification:', error);
        // Keep the temp notification but mark it as not persisted
        return tempId;
      }
    }

    return tempId;
  }, [keycloak]);

  const updateNotification = useCallback(async (id, updates) => {
    // Optimistically update local state
    setNotifications((prev) =>
      prev.map((notif) => (notif.id === id ? { ...notif, ...updates } : notif))
    );

    // Update in backend if authenticated and not a temp ID
    if (keycloak?.authenticated && !id.toString().startsWith('temp_')) {
      try {
        await apiUpdateNotification(keycloak, id, updates);
      } catch (error) {
        console.error('Failed to update notification:', error);
        // Reload to get correct state
        loadNotifications();
      }
    }
  }, [keycloak, loadNotifications]);

  const dismissNotification = useCallback(async (id) => {
    // Optimistically update local state
    setNotifications((prev) =>
      prev.map((notif) => (notif.id === id ? { ...notif, dismissed: true } : notif))
    );

    // Update in backend if authenticated and not a temp ID
    if (keycloak?.authenticated && !id.toString().startsWith('temp_')) {
      try {
        await apiDismissNotification(keycloak, id);
      } catch (error) {
        console.error('Failed to dismiss notification:', error);
        // Reload to get correct state
        loadNotifications();
      }
    }
  }, [keycloak, loadNotifications]);

  const removeNotification = useCallback(async (id) => {
    // Optimistically remove from local state
    setNotifications((prev) => prev.filter((notif) => notif.id !== id));

    // Delete from backend if authenticated and not a temp ID
    if (keycloak?.authenticated && !id.toString().startsWith('temp_')) {
      try {
        await apiDeleteNotification(keycloak, id);
      } catch (error) {
        console.error('Failed to delete notification:', error);
        // Reload to get correct state
        loadNotifications();
      }
    }
  }, [keycloak, loadNotifications]);

  const clearAll = useCallback(async () => {
    setNotifications([]);
    if (keycloak?.authenticated) {
      try {
        await apiClearAllNotifications(keycloak);
      } catch (error) {
        console.error('Failed to clear notifications:', error);
        loadNotifications();
      }
    }
  }, [keycloak, loadNotifications]);

  const getUnreadCount = useCallback(() => {
    return notifications.filter((n) => !n.dismissed && n.status !== 'completed' && n.status !== 'error').length;
  }, [notifications]);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        loading,
        addNotification,
        updateNotification,
        dismissNotification,
        removeNotification,
        clearAll,
        getUnreadCount,
        loadNotifications,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};
